import React from 'react';
import Moment from 'moment';
import { t } from '../../../../i18n';
import { formatNumber } from '../../normalizer';
import CustomDataTable from '../../shared/CustomDataTable';
import InputKeyValue from '../../shared/InputKeyValue';
import CustomButton from '../../shared/CustomButton';

/**
 * Get status label and disabled state from status string
 */
const getStatusInfo = (status) => {
  const normalizedStatus = (status || '').toLowerCase();

  switch (normalizedStatus) {
    case 'enviado':
      return { statusLabel: 'sent', disabled: false };
    case 'entregue':
      return { statusLabel: 'delivered', disabled: true };
    default:
      return { statusLabel: 'processing', disabled: true };
  }
};

/**
 * Orders Table component using CustomDataTable
 */
const OrdersTable = (props) => {
  const {
    currencyLabel,
    orders,
    pagination,
    page,
    onPageChange,
    getOrderPaymentInfo,
  } = props;

  const className = 'orders-table';

  // Column definitions
  const columns = [
    {
      key: 'number',
      label: t('gamification2026.orders-table.order-number'),
      sortKey: 'number',
    },
    {
      key: 'checkout_date',
      label: t('gamification2026.orders-table.date'),
      sortKey: 'checkout_date',
      render: (_v, item) => new Moment(item.checkout_date).format(t('date-format')),
    },
    {
      key: 'total_points',
      label: currencyLabel,
      sortKey: 'total_points',
      render: (_v, item) => formatNumber(item.total_points),
    },
    {
      key: 'status',
      label: t('gamification2026.orders-table.state'),
      sortKey: 'status',
      render: (_v, item) => {
        const { statusLabel } = getStatusInfo(item.status);
        return (
          <span className={`${className}__cell--status--${statusLabel}`}>
            {item.status}
          </span>
        );
      },
    },
    {
      key: 'payment_info',
      label: t('gamification2026.orders-table.info'),
      sortKey: 'payment_info',
      sortable: false,
      render: (_v, item) => getOrderPaymentInfo(item),
    },
    {
      key: 'received',
      label: t('gamification2026.orders-table.received'),
      sortKey: 'received',
      sortable: false,
      render: (_v, item) => {
        const { disabled } = getStatusInfo(item.status);
        return (
          <InputKeyValue
            disabled={disabled}
            type="checkbox"
            checked={item.received_by_user}
            readOnly
          />
        );
      },
    },
    {
      key: 'actions',
      label: ' ',
      sortKey: 'actions',
      sortable: false,
      render: (_v, item) => (
        <CustomButton link tight medium to={`/profile/orders/${item.id}`}>
          Ver detalhes
        </CustomButton>
      ),
    },
  ];

  // Sort function
  const sortFn = (data, sort) => {
    return [...data].sort((a, b) => {
      const multiplier = sort.direction === 'asc' ? 1 : -1;
      if (sort.key === 'checkout_date') {
        return (new Date(a.checkout_date) - new Date(b.checkout_date)) * multiplier;
      }
      if (sort.key === 'total_points') {
        return (parseFloat(a.total_points) - parseFloat(b.total_points)) * multiplier;
      }
      if (sort.key === 'status') {
        const descA = (a.status || '').toLowerCase();
        const descB = (b.status || '').toLowerCase();
        return descA.localeCompare(descB) * multiplier;
      }
      if (sort.key === 'number') {
        const descA = (a.number || '').toLowerCase();
        const descB = (b.number || '').toLowerCase();
        return descA.localeCompare(descB) * multiplier;
      }
      if (sort.key === 'received') {
        return (a.received_by_user === b.received_by_user ? 0 : a.received_by_user ? 1 : -1) * multiplier;
      }
      return 0;
    });
  };

  // Custom row renderer
  const renderRow = (order, index, className) => {
    const { statusLabel, disabled } = getStatusInfo(order.status);

    return (
      <tr
        key={order.id || index}
        className={`data-table__row ${className}__row`}
      >
        <td className={`data-table__cell ${className}__cell ${className}__cell--number`}>
          {order.number}
        </td>
        <td className={`data-table__cell ${className}__cell ${className}__cell--checkout_date`}>
          {new Moment(order.checkout_date).format(t('date-format'))}
        </td>
        <td className={`data-table__cell ${className}__cell ${className}__cell--total_points`}>
          {formatNumber(order.total_points)}
        </td>
        <td className={`data-table__cell ${className}__cell ${className}__cell--status`}>
          <span className={`${className}__cell--status--${statusLabel}`}>
            {order.status}
          </span>
        </td>
        <td className={`data-table__cell ${className}__cell ${className}__cell--payment_info`}>
          {getOrderPaymentInfo(order)}
        </td>
        <td className={`data-table__cell ${className}__cell ${className}__cell--received`}>
          <InputKeyValue
            disabled={disabled}
            type="checkbox"
            checked={order.received_by_user}
            readOnly
          />
        </td>
         <td className={`data-table__cell ${className}__cell ${className}__cell--actions`}>
          <CustomButton link tight medium to={`/profile/orders/${order.id}`}>
            Ver detalhes
          </CustomButton>
        </td>
      </tr>
    );
  };

  return (
    <CustomDataTable
      className="orders-table"
      columns={columns}
      data={orders}
      renderRow={renderRow}
      rowKey={(item) => item.id}
      defaultSort={{ key: 'checkout_date', direction: 'desc' }}
      sortFn={sortFn}
      pagination={{
        totalPages: pagination.total_pages,
        currentPage: page,
        onPageChange,
      }}
      emptyMessage={t('gamification2026.orders-table.no-results')}
    />
  );
};

export default OrdersTable;
